import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import api, { formatTL, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { CreditCard, Banknote, CheckCircle2, Bus } from "lucide-react";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";

const inputCls =
  "w-full rounded-lg border border-slate-200 px-4 py-2.5 text-sm focus:outline-none focus:border-[#0077B6]";

export default function Checkout() {
  const { items, subtotal, clear } = useCart();
  const { user } = useAuth();
  const { settings } = useSettings();
  const location = useLocation();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    full_name: user?.name || "",
    phone: user?.phone || "",
    email: user?.email || "",
    otogar: "",
    notes: "",
  });
  const [payment, setPayment] = useState("bank_transfer");
  const [loading, setLoading] = useState(false);
  const [orderResult, setOrderResult] = useState(null);

  const busFee = settings.bus_fee || 400;
  const total = subtotal + busFee;

  const handle = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!items.length) return;
    if (!form.otogar.trim()) { toast.error("Lütfen teslim alacağınız otogarı yazın"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/orders", {
        items: items.map((it) => ({ product_id: it.product_id, quantity: it.quantity })),
        delivery: {
          full_name: form.full_name,
          phone: form.phone,
          email: form.email,
          otogar: form.otogar,
          notes: form.notes,
        },
        payment_method: payment,
        coupon_code: location.state?.coupon || null,
      });
      // Online ödeme seçildiyse PayTR iframe sayfasına yönlendir
      if (payment === "credit_card") {
        try {
          const tokenRes = await api.post("/paytr/create-token", { order_no: data.order_no });
          clear();
          window.location.href = tokenRes.data.iframe_url;
          return;
        } catch (err) {
          toast.error("PayTR ödeme sayfası açılamadı: " + formatApiError(err));
          // sipariş zaten oluşturuldu, ama PayTR token alınamadı
          setOrderResult(data);
          clear();
          return;
        }
      }
      setOrderResult(data);
      clear();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (orderResult) {
    return (
      <div data-testid="order-success" className="container-max px-4 md:px-8 section-pad max-w-2xl text-center">
        <CheckCircle2 size={64} className="mx-auto text-emerald-600 mb-5" />
        <h1 className="font-display text-3xl md:text-4xl font-extrabold mb-3">Siparişiniz Alındı</h1>
        <p className="text-slate-600 mb-2">Sipariş numaranız:</p>
        <div data-testid="order-no" className="font-display text-2xl font-extrabold text-[#0077B6] mb-6">{orderResult.order_no}</div>
        <div className="bg-white rounded-2xl border border-slate-100 p-6 text-left text-sm space-y-2 mb-6">
          <div className="flex justify-between"><span className="text-slate-600">Toplam</span><span className="font-bold">{formatTL(orderResult.total)}</span></div>
          <div className="flex justify-between"><span className="text-slate-600">Otobüs ücreti</span><span className="font-bold">{formatTL(orderResult.bus_fee)}</span></div>
          <div className="flex justify-between"><span className="text-slate-600">Teslim alınacak otogar</span><span className="font-bold">{orderResult.delivery?.otogar}</span></div>
          <div className="flex justify-between"><span className="text-slate-600">Ödeme</span><span className="font-bold">{
            { credit_card: "Online Ödeme (Kredi Kartı)", bank_transfer: "Havale / EFT" }[orderResult.payment_method]
          }</span></div>
          {orderResult.payment_method === "bank_transfer" && (
            <div className="mt-3 text-xs text-slate-600 bg-[#F4F7F9] rounded-lg p-3">
              <div className="font-bold mb-1">Havale / EFT Bilgileri</div>
              IBAN: <strong>TR15 0015 7000 0000 0072 5444 02</strong><br/>
              Alıcı Adı: <strong>Tuba Vural</strong><br/>
              Banka: <strong>EnPara (QNB Finansbank)</strong><br/>
              Açıklama: <strong>{orderResult.order_no}</strong>
            </div>
          )}
        </div>
        <div className="bg-[#0077B6]/5 border border-[#0077B6]/20 rounded-2xl p-5 text-left text-sm mb-8">
          <div className="flex items-start gap-3">
            <Bus className="text-[#0077B6] mt-0.5" size={20}/>
            <div>
              <div className="font-bold mb-1">Otogardan Teslim Bilgilendirmesi</div>
              <p className="text-slate-700">Siparişiniz aynı gün otobüsle yola çıkarılacak ve otogara ulaştığında <strong>{form.phone}</strong> telefon numaranız üzerinden WhatsApp ile bilgi verilecektir. Lütfen otogarın bagaj/kargo bölümünden sipariş numaranız ile teslim alınız.</p>
            </div>
          </div>
        </div>
        <button onClick={() => navigate("/")} className="btn-primary">Anasayfaya Dön</button>
      </div>
    );
  }

  if (!items.length) {
    return (
      <div className="container-max section-pad text-center">
        <p className="text-slate-600">Sepetiniz boş.</p>
        <button onClick={() => navigate("/urunler")} className="btn-primary mt-4">Ürünlere Git</button>
      </div>
    );
  }

  return (
    <div data-testid="checkout-page" className="container-max px-4 md:px-8 section-pad">
      <h1 className="font-display text-3xl md:text-4xl font-extrabold mb-8 tracking-tight">Sipariş Tamamla</h1>
      <form onSubmit={submit} className="grid lg:grid-cols-12 gap-8">
        <div className="lg:col-span-8 space-y-6">
          <div className="bg-white rounded-2xl border border-slate-100 p-6">
            <h2 className="font-display text-lg font-extrabold mb-1">Teslim Alacağınız Otogar <span className="text-rose-600">*</span></h2>
            <p className="text-xs text-slate-500 mb-4">Siparişiniz otobüs ile bu otogara gönderilir, otogardan sipariş numaranız ile teslim alacaksınız.</p>
            <input
              data-testid="otogar-input"
              required
              placeholder="Örn: İstanbul Esenler Otogarı"
              className={inputCls}
              value={form.otogar}
              onChange={handle("otogar")}
            />
            <div className="mt-3 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
              <Bus size={16} className="mt-0.5"/>
              <span>Otobüs ücreti <strong>{formatTL(busFee)}</strong> olarak siparişinize eklenir.</span>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-slate-100 p-6">
            <h2 className="font-display text-lg font-extrabold mb-1">İletişim Bilgileri <span className="text-rose-600">*</span></h2>
            <p className="text-xs text-slate-500 mb-4">Sipariş otogara ulaştığında WhatsApp ve e-posta ile bilgilendirileceksiniz. Lütfen doğru bilgi giriniz.</p>
            <div className="grid sm:grid-cols-2 gap-3">
              <input data-testid="addr-name" required placeholder="Ad Soyad" className={inputCls} value={form.full_name} onChange={handle("full_name")} />
              <input data-testid="addr-phone" required placeholder="Telefon (örn: 0532 123 45 67)" className={inputCls} value={form.phone} onChange={handle("phone")} />
              <input data-testid="addr-email" required type="email" placeholder="E-posta" className={inputCls + " sm:col-span-2"} value={form.email} onChange={handle("email")} />
              <textarea data-testid="addr-notes" placeholder="Sipariş notu (opsiyonel)" className={inputCls + " sm:col-span-2"} rows={2} value={form.notes} onChange={handle("notes")} />
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-slate-100 p-6">
            <h2 className="font-display text-lg font-extrabold mb-4">Ödeme Yöntemi</h2>
            <RadioGroup value={payment} onValueChange={setPayment} className="space-y-2">
              {[
                { v: "bank_transfer", icon: Banknote, label: "Havale / EFT", desc: "Sipariş sonrası IBAN bilgisi gönderilir" },
                { v: "credit_card", icon: CreditCard, label: "Online Ödeme (Kredi Kartı)", desc: "Tüm bankalar, 3D Secure güvenli ödeme" },
              ].map(({ v, icon: Icon, label, desc }) => (
                <Label key={v} htmlFor={v} className={`flex items-center gap-3 rounded-xl border-2 p-4 cursor-pointer ${payment === v ? "border-[#0077B6] bg-[#0077B6]/5" : "border-slate-200"}`}>
                  <RadioGroupItem id={v} value={v} data-testid={`pay-${v}`} />
                  <Icon size={20} className="text-[#0077B6]" />
                  <div>
                    <div className="font-semibold">{label}</div>
                    <div className="text-xs text-slate-500">{desc}</div>
                  </div>
                </Label>
              ))}
            </RadioGroup>
            {payment === "credit_card" && (
              <div className="mt-4 bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-xs text-emerald-800">
                Siparişi Tamamla butonuna basınca PayTR güvenli ödeme sayfasına yönlendirileceksiniz. Tüm kart bilgileriniz PayTR tarafından şifrelenerek alınır.
              </div>
            )}
          </div>
        </div>

        <aside className="lg:col-span-4">
          <div className="bg-white rounded-2xl border border-slate-100 p-6 sticky top-24">
            <h2 className="font-display text-lg font-extrabold mb-4">Sipariş Özeti</h2>
            <div className="space-y-2 text-sm mb-4">
              {items.map((it) => (
                <div key={it.product_id} className="flex justify-between gap-2">
                  <span className="text-slate-600 truncate">{it.name} × {it.quantity}</span>
                  <span className="font-semibold whitespace-nowrap">{formatTL(it.price * it.quantity)}</span>
                </div>
              ))}
            </div>
            <div className="border-t pt-3 space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-slate-600">Ara toplam</span><span>{formatTL(subtotal)}</span></div>
              <div className="flex justify-between"><span className="text-slate-600 flex items-center gap-1.5"><Bus size={14}/> Otobüs ücreti</span><span>{formatTL(busFee)}</span></div>
              <div className="flex justify-between font-display text-xl font-extrabold pt-2"><span>Toplam</span><span className="text-[#0077B6]">{formatTL(total)}</span></div>
            </div>
            <button data-testid="place-order" disabled={loading} className="btn-primary w-full mt-6 disabled:opacity-50">
              {loading ? "Sipariş oluşturuluyor..." : "Siparişi Tamamla"}
            </button>
          </div>
        </aside>
      </form>
    </div>
  );
}
